import { useState } from 'react';
import { Copy, Check, Printer, FileDown } from 'lucide-react';
import { exportNodeAsPdf } from '@/lib/pdfExport';
import PdfModeModal, { getSavedPdfMode } from '@/components/PdfModeModal';

/**
 * SectionActions — Feb 2026.
 *
 * Reusable Copy / Print / PDF action cluster, modelled on the
 * FilingViewer header trio. Mounts inside any section header that
 * surfaces structured data (insider trades, 13-F holders, etc.) so
 * users can quickly hand the section's content to their notes app,
 * printer, or accountant.
 *
 *   • targetRef: ref pointing at the DOM node whose `innerText` /
 *     HTML drives Copy + Print. The Copy button uses `innerText`
 *     (preserves visual reading order through tables/lists).
 *   • elementId: DOM `id` on the wrapper that exportNodeAsPdf will
 *     `getElementById` for the PDF capture. Must be unique on the
 *     page; pass `insider-form4-${ticker}` etc.
 *   • title: passed into Print + PDF for the window/filename label.
 *   • user: forwarded to exportNodeAsPdf so the "Generated for X"
 *     footer stamp matches every other PDF in the app.
 */
export default function SectionActions({
  targetRef,
  elementId,
  title,
  filename,
  user = null,
  testid = 'section-actions',
}) {
  const [copied, setCopied] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  const slug = (filename || title || 'section')
    .toString()
    .toLowerCase()
    .replace(/[^\w]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const handleCopy = async () => {
    const node = targetRef?.current;
    if (!node) return;
    const text = node.innerText || '';
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Legacy fallback for browsers without clipboard permission.
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch { /* swallow */ }
    }
  };

  const handlePrint = () => {
    const node = targetRef?.current;
    if (!node) return;
    // Same popup strategy as FilingViewer — open a clean window and
    // print ONLY the section's HTML, not the surrounding modal/app
    // chrome. We MUST NOT pass `noopener` here (Chrome returns null
    // from window.open and breaks the document.write fallback).
    const win = window.open('', '_blank', 'width=900,height=1100');
    if (!win) {
      alert('Print blocked: please allow pop-ups for this site so the section can open in its own window for printing.');
      return;
    }
    win.document.open();
    win.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${(title || 'Section').replace(/[<>]/g, '')}</title>
<style>
  body { font-family: Helvetica, Arial, sans-serif; color: #111; margin: 24px; line-height: 1.45; }
  h1, h2, h3, h4 { font-family: Helvetica, Arial, sans-serif; }
  table { border-collapse: collapse; margin: 12px 0; width: 100%; font-size: 11px; }
  table td, table th { border: 1px solid #ccc; padding: 4px 6px; vertical-align: top; }
  thead { background: #f4f4f4; }
  .section-print-meta { font-size: 11px; color: #666; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 16px; }
  .section-print-meta strong { color: #111; }
  @page { margin: 18mm 14mm 18mm 14mm; }
</style>
</head>
<body>
<div class="section-print-meta">
  <strong>${(title || 'Section').replace(/[<>]/g, '')}</strong>
  &nbsp;·&nbsp; Printed via twylightcrow.com on ${new Date().toLocaleDateString()}
</div>
${node.outerHTML}
</body>
</html>`);
    win.document.close();
    setTimeout(() => {
      try {
        win.focus();
        win.print();
      } catch { /* user-cancelled */ }
    }, 400);
  };

  const handlePdf = async (mode = 'download') => {
    if (!elementId) return;
    setPdfBusy(true);
    try {
      await exportNodeAsPdf({
        elementId,
        filename: `${slug || 'section'}.pdf`,
        mode,
        user,
      });
    } finally {
      setPdfBusy(false);
    }
  };
  const onPdfClick = () => {
    const saved = getSavedPdfMode();
    if (saved) {
      handlePdf(saved);
    } else {
      setPdfModalOpen(true);
    }
  };

  return (
    <>
      <div className="inline-flex items-center gap-3" data-testid={testid}>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
          data-testid={`${testid}-copy`}
          title={`Copy ${title || 'section'} to clipboard`}
        >
          {copied ? (
            <>
              <Check className="w-3 h-3 text-emerald-500" />
              Copied
            </>
          ) : (
            <>
              <Copy className="w-3 h-3" />
              Copy
            </>
          )}
        </button>
        <button
          type="button"
          onClick={handlePrint}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
          data-testid={`${testid}-print`}
          title={`Print ${title || 'section'} (opens a clean print window)`}
        >
          <Printer className="w-3 h-3" />
          Print
        </button>
        <button
          type="button"
          onClick={onPdfClick}
          disabled={pdfBusy}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testid}-pdf`}
          title={`Save ${title || 'section'} as PDF`}
        >
          <FileDown className="w-3 h-3" />
          {pdfBusy ? 'Saving…' : 'PDF'}
        </button>
      </div>

      <PdfModeModal
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        onApply={(mode) => { setPdfModalOpen(false); handlePdf(mode); }}
        title={`Save ${title || 'Section'} PDF`}
      />
    </>
  );
}
